import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Tags } from "lucide-react";

const emptyForm = { name: "", slug: "", description: "", sort_order: 0, status: "active" };

const slugify = (value = "") => value
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");


const getDeleteMessage = (response, fallback) => {
  const data = response?.data || {};
  if (data.action === "soft_deleted") return data.message || fallback;
  return data.message || fallback;
};

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchCategories = () => API.get("/categories?include_inactive=true")
    .then((r) => setCategories(r.data.categories || []))
    .catch(() => toast.error("Gagal memuat kategori"));

  useEffect(() => { fetchCategories(); }, []);

  const filteredCategories = categories.filter((c) =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.slug?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.description || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (category) => {
    setEditing(category.id);
    setForm({
      name: category.name || "",
      slug: category.slug || "",
      description: category.description || "",
      sort_order: category.sort_order || 0,
      status: category.status || "active",
    });
    setOpen(true);
  };

  const handleNameChange = (value) => {
    setForm((prev) => ({ ...prev, name: value, slug: editing ? prev.slug : slugify(value) }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Nama kategori wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.name), sort_order: Number(form.sort_order || 0) };
      if (editing) await API.put(`/categories/${editing}`, payload);
      else await API.post("/categories", payload);
      toast.success("Kategori tersimpan");
      setOpen(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan kategori");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Hapus kategori ${category.name}? Produk yang sudah memakai kategori ini tidak ikut dihapus.`)) return;
    try {
      const response = await API.delete(`/categories/${category.id}`);
      toast.success(getDeleteMessage(response, "Kategori dihapus"));
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus kategori");
    }
  };

  return (
    <AdminLayout title="Master Kategori">
      <div data-testid="admin-categories">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <p className="text-sm text-stone-500">Kelola kategori produk untuk dropdown produk, catalog, dan filter.</p>
            <p className="text-xs text-stone-400 mt-1">{filteredCategories.length} kategori</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Cari kategori" className="pl-9 h-9" />
            </div>
            <button onClick={openCreate} className="bg-[#8A9A86] text-white px-4 h-9 text-sm flex items-center justify-center gap-2 hover:bg-[#768573] rounded-md transition-colors whitespace-nowrap">
              <Plus className="w-4 h-4" /> Tambah Kategori
            </button>
          </div>
        </div>

        <div className="bg-white border border-stone-100 rounded-xl overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_0.7fr_0.7fr] gap-4 px-4 py-3 border-b border-stone-100 text-xs uppercase tracking-wider text-stone-500 font-semibold">
            <div>Nama</div><div>Slug</div><div>Deskripsi</div><div>Status</div><div className="text-right">Aksi</div>
          </div>
          {filteredCategories.length === 0 ? (
            <div className="p-10 text-center text-stone-500">
              <Tags className="w-8 h-8 mx-auto mb-2 text-stone-300" />
              Belum ada kategori.
            </div>
          ) : filteredCategories.map((category) => (
            <div key={category.id} className="grid md:grid-cols-[1.5fr_1fr_1fr_0.7fr_0.7fr] gap-3 md:gap-4 px-4 py-4 border-b border-stone-100 last:border-b-0 items-center">
              <div>
                <p className="font-medium text-stone-900">{category.name}</p>
                <p className="md:hidden text-xs text-stone-400">{category.slug}</p>
              </div>
              <div className="hidden md:block text-sm text-stone-500">{category.slug}</div>
              <div className="text-sm text-stone-500 truncate">{category.description || "-"}</div>
              <div>
                <span className={`inline-flex px-2 py-1 rounded-full text-[10px] font-semibold uppercase ${category.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                  {category.status === "active" ? "Aktif" : "Nonaktif"}
                </span>
              </div>
              <div className="flex justify-end gap-1">
                <button onClick={() => openEdit(category)} className="p-2 text-stone-400 hover:text-stone-700"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(category)} className="p-2 text-stone-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Edit Kategori" : "Kategori Baru"}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Nama Kategori</Label>
                <Input value={form.name} onChange={(e) => handleNameChange(e.target.value)} className="mt-1" autoFocus />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Slug</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="mt-1" placeholder="contoh: gamis" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Deskripsi</Label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 focus:outline-none focus:border-[#8A9A86]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Urutan</Label>
                  <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <button onClick={handleSave} disabled={saving} className="w-full bg-[#8A9A86] text-white py-3 text-sm uppercase tracking-wider hover:bg-[#768573] disabled:opacity-60">
                {saving ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
